import {
  FinanceModel,
  FinanceModelTypes,
  PeripheralFinanceModel,
  PeripheralFinanceModelTypes
} from '@/services/api/contracts/financemodels';
import { IncomingFinanceModel } from '@/services/csv/helpers/parsers';

export enum BulkFinanceModelTypes {
  FMV = 'FMV',
  FPL = 'FPL'
}

export const BULK_FINANCE_MODEL_PERIPHERAL_CATEGORY_ID = 'peripheral';

export const mapBulkFinanceModelTypeToModelAPIType = (type: BulkFinanceModelTypes) => {
  switch (type) {
    case BulkFinanceModelTypes.FMV:
      return FinanceModelTypes.FMV;
    case BulkFinanceModelTypes.FPL:
      return FinanceModelTypes.FPL;
  }
};

export const mapBulkFinanceModelTypeToPeripheralModelAPIType = (type: BulkFinanceModelTypes) => {
  if (type === BulkFinanceModelTypes.FPL) return PeripheralFinanceModelTypes.FPL;

  throw new Error('Invalid Peripheral model type');
};

export const mapAPIModelTypeToBulkFinanceModelType = (type: FinanceModelTypes) => {
  switch (type) {
    case FinanceModelTypes.FMV:
      return BulkFinanceModelTypes.FMV;
    case FinanceModelTypes.FPL:
      return BulkFinanceModelTypes.FPL;
  }

  throw new Error('Invalid Finance model type');
};

export const mapPeripheralAPIModelTypeToBulkFinanceModelType = (type: PeripheralFinanceModelTypes) => {
  if (type === PeripheralFinanceModelTypes.FPL) return BulkFinanceModelTypes.FPL;

  throw new Error('Invalid Peripheral model type');
};

export const getPeripheralModelTypeString = (type: PeripheralFinanceModelTypes) => {
  if (type === PeripheralFinanceModelTypes.FPL) return 'FPL';

  throw new Error('Invalid Peripheral model type');
};

export const getModelTypeString = (type: FinanceModelTypes) => {
  switch (type) {
    case FinanceModelTypes.FMV:
      return 'FMV';
    case FinanceModelTypes.FPL:
      return 'FPL';
  }

  throw new Error('Invalid Finance model type');
};

export const getCSVModelTypeString = (type: BulkFinanceModelTypes) => {
  switch (type) {
    case BulkFinanceModelTypes.FMV:
      return 'FMV';
    case BulkFinanceModelTypes.FPL:
      return 'FPL';
  }
};

export const constructNameFromTypeAndTerm = (type: FinanceModelTypes, term?: string | number) => {
  return `${getModelTypeString(type)}${term ? ` ${term}yr` : ''}`;
};

export const constructPeripheralNameFromTypeAndTerm = (type: PeripheralFinanceModelTypes, term?: string | number) => {
  return `${getPeripheralModelTypeString(type)}${term ? ` ${term}yr` : ''}`;
};

export const constructCSVNameFromTypeAndTerm = (type: BulkFinanceModelTypes, term?: string | number) => {
  return `${getCSVModelTypeString(type)}${term ? ` ${term}yr` : ''}`;
};

export const constructModelIdentifier = (model: FinanceModel) => {
  return `${constructNameFromTypeAndTerm(
    model.financeModelType as unknown as FinanceModelTypes,
    model.term
  ).toLowerCase()}-${model.category.name.toLowerCase()}${
    model.manufacturer.name ? `-${model.manufacturer.name.toLowerCase()}` : ''
  }`;
};

export const constructPeripheralModelIdentifier = (model: PeripheralFinanceModel) => {
  return `${constructPeripheralNameFromTypeAndTerm(
    model.peripheralFinanceModelType,
    model.term
  ).toLowerCase()}-${BULK_FINANCE_MODEL_PERIPHERAL_CATEGORY_ID}`;
};

export const constructCSVModelIdentifier = (model: IncomingFinanceModel) => {
  return `${constructCSVNameFromTypeAndTerm(
    model.Type as unknown as BulkFinanceModelTypes,
    model.Term
  ).toLowerCase()}-${model.Category.toLowerCase()}${model.Manufacturer ? `-${model.Manufacturer.toLowerCase()}` : ''}`;
};

export const getDuplicateEntries = (models: IncomingFinanceModel[]) => {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  models.forEach(item => {
    const name = constructCSVModelIdentifier(item);

    if (seen.has(name)) {
      duplicates.add(name);
    } else {
      seen.add(name);
    }
  });

  return Array.from(duplicates);
};

export const isPeripheralModel = (model: IncomingFinanceModel) => {
  return model.Category.toLowerCase() === BULK_FINANCE_MODEL_PERIPHERAL_CATEGORY_ID;
};

const buildCsvPcmIdentifier = (model: IncomingFinanceModel) =>
  model.Pcms.map(p => `${p.type}-${p.value ?? 0}`).join(',');

const buildPcmIdentifier = (model: FinanceModel | PeripheralFinanceModel) =>
  model.pcms.map(p => `${p.region}-${p.value ?? 0}`).join(',');

export const filterNonIdenticalBulkModels = (
  incomingModels: IncomingFinanceModel[],
  financeModels: FinanceModel[],
  peripheralModels: PeripheralFinanceModel[]
) => {
  const isModelIdentical = (csvModel: IncomingFinanceModel, model: FinanceModel) => {
    const csvPcmString = buildCsvPcmIdentifier(csvModel);
    const modelPcmString = buildPcmIdentifier(model);

    return (
      `${constructModelIdentifier(model)}${modelPcmString}` ===
      `${constructCSVModelIdentifier(csvModel)}${csvPcmString}`
    );
  };

  const isPeripheralModelIdentical = (csvModel: IncomingFinanceModel, model: PeripheralFinanceModel) => {
    const csvPcmString = buildCsvPcmIdentifier(csvModel);
    const modelPcmString = buildPcmIdentifier(model);

    return (
      `${constructPeripheralModelIdentifier(model)}${modelPcmString}` ===
      `${constructCSVModelIdentifier(csvModel)}${csvPcmString}`
    );
  };

  const incomingDeviceModels = incomingModels.filter(
    model => model.Category.toLowerCase() !== BULK_FINANCE_MODEL_PERIPHERAL_CATEGORY_ID
  );
  const incomingPeripheralModels = incomingModels.filter(
    model => model.Category.toLowerCase() === BULK_FINANCE_MODEL_PERIPHERAL_CATEGORY_ID
  );

  const duplicateDeviceModels = incomingDeviceModels.filter((incoming: IncomingFinanceModel) => {
    return financeModels.some((model: FinanceModel) => {
      return isModelIdentical(incoming, model);
    });
  });

  const duplicatePeripheralModels = incomingPeripheralModels.filter((incoming: IncomingFinanceModel) => {
    return peripheralModels.some((model: PeripheralFinanceModel) => {
      return isPeripheralModelIdentical(incoming, model);
    });
  });

  const nonDuplicateDeviceModels = incomingDeviceModels.filter(
    model =>
      !duplicateDeviceModels.some(
        duplicate => constructCSVModelIdentifier(duplicate) === constructCSVModelIdentifier(model)
      )
  );

  const nonDuplicatePeripheralModels = incomingPeripheralModels.filter(
    model =>
      !duplicatePeripheralModels.some(
        duplicate => constructCSVModelIdentifier(duplicate) === constructCSVModelIdentifier(model)
      )
  );

  return {
    mainDevices: nonDuplicateDeviceModels,
    peripherals: nonDuplicatePeripheralModels
  };
};
